import type { Dm2016ReferenceInterpretationMode } from "@rcprofessionale/calc-core";

import {
  calculateDm2016LabWithHashes,
  getDm2016LabCatalog,
  getDm2016LabServices,
  type Dm2016LabService,
  type HashedDm2016LabCalculationResult,
} from "./dm2016-application.js";

export interface Dm2016LabControllerOptions {
  readonly onInvalid?: () => void;
  readonly onResult?: (result: HashedDm2016LabCalculationResult) => void;
}

export interface Dm2016LabDraft {
  readonly categoryCode: string;
  readonly destinationCode: string;
  readonly serviceCode: string;
  readonly valueV: string;
  readonly inhabitants: string;
  readonly multiplier: string;
  readonly planningLevel?: "general" | "executive";
  readonly planningValueBasis?: "territorial_gdp" | "existing_and_design_volumes" | "farm_gross_saleable_production";
  readonly procurementModel?: "non_integrated" | "integrated";
  readonly tenderBaseProject?: boolean;
  readonly publicWorksConcession?: boolean;
  readonly interpretationMode: "printed_source_strict" | "professionearchitetto_v0_74";
}

export interface Dm2016LabController {
  captureDraft(): Dm2016LabDraft;
  restoreDraft(draft: Dm2016LabDraft): boolean;
  setEnabled(enabled: boolean, reason?: string): void;
}

function option(value: string, label: string): HTMLOptionElement {
  const element = document.createElement("option");
  element.value = value;
  element.textContent = label;
  return element;
}

function formatDecimal(value: string, minimumDecimals = 0): string {
  const [integer = "0", decimal = ""] = value.split(".");
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const padded = decimal.padEnd(minimumDecimals, "0");
  return padded.length === 0 ? grouped : `${grouped},${padded}`;
}

function formatDecimalLimited(value: string, maximumDecimals: number): string {
  const [integer = "0", decimal = ""] = value.split(".");
  const limited = decimal.slice(0, maximumDecimals).replace(/0+$/, "");
  return formatDecimal(limited.length === 0 ? integer : `${integer}.${limited}`);
}

function euro(value: string): string {
  return `€ ${formatDecimal(value, 2)}`;
}

function modeLabel(mode: Dm2016ReferenceInterpretationMode): string {
  return mode === "printed_source_strict"
    ? "Testo letterale del D.M. 17/06/2016"
    : "Compatibilità CNI / ProfessioneArchitetto v0.74";
}

function addDefinitionRow(list: HTMLDListElement, term: string, value: string): void {
  const row = document.createElement("div");
  const dt = document.createElement("dt");
  const dd = document.createElement("dd");
  dt.textContent = term;
  dd.textContent = value;
  row.append(dt, dd);
  list.append(row);
}

function renderResult(
  root: HTMLElement,
  result: HashedDm2016LabCalculationResult,
): void {
  const output = root.querySelector<HTMLElement>("[data-dm2016-output]");
  const amount = root.querySelector<HTMLElement>("[data-dm2016-amount]");
  const summary = root.querySelector<HTMLDListElement>("[data-dm2016-summary]");
  const trace = root.querySelector<HTMLTableSectionElement>("[data-dm2016-trace]");
  const warnings = root.querySelector<HTMLUListElement>("[data-dm2016-warnings]");
  const warningBox = root.querySelector<HTMLElement>("[data-dm2016-warning-box]");
  const source = root.querySelector<HTMLElement>("[data-dm2016-source]");
  if (
    output === null || amount === null || summary === null || trace === null
    || warnings === null || warningBox === null || source === null
  ) return;

  amount.textContent = euro(result.reference.calculation.compensationRounded);
  summary.replaceChildren();
  addDefinitionRow(summary, "Destinazione", `${result.destination.code} · G ${formatDecimal(result.destination.complexityG)}`);
  addDefinitionRow(summary, "Prestazione", `${result.service.code} · ${result.service.label}`);
  addDefinitionRow(summary, "Valore V", euro(result.reference.calculation.valueV));
  if (result.normalizedInput.inhabitants !== null) {
    addDefinitionRow(summary, "Popolazione", `${formatDecimal(result.normalizedInput.inhabitants)} abitanti`);
  }
  if (result.normalizedInput.footnoteContext.planning !== null) {
    const planning = result.normalizedInput.footnoteContext.planning;
    addDefinitionRow(
      summary,
      "Pianificazione",
      planning.level === "general"
        ? "Piano generale"
        : "Piano esecutivo o attuativo",
    );
    const basisLabels = {
      territorial_gdp: "PIL del territorio interessato",
      existing_and_design_volumes: "Volumi esistenti e di progetto",
      farm_gross_saleable_production: "Produzione lorda vendibile fondiaria",
    } as const;
    addDefinitionRow(
      summary,
      "Base del valore V",
      basisLabels[planning.valueBasis],
    );
  }
  if (result.normalizedInput.multiplier !== "1") {
    addDefinitionRow(summary, "Moltiplicatore", formatDecimal(result.normalizedInput.multiplier));
  }
  const footnotes = result.footnoteResolution.services[0]?.appliedFootnotes ?? [];
  if (footnotes.length > 0) {
    addDefinitionRow(summary, "Note applicate", footnotes.map((item) => `n. ${item}`).join(", "));
  }
  addDefinitionRow(summary, "Interpretazione", modeLabel(result.reference.resolution.interpretationMode));
  addDefinitionRow(summary, "Ruleset", `${result.rulesetId} · ${result.rulesetVersion}`);
  addDefinitionRow(
    summary,
    "Impronta calcolo",
    `${result.resultSha256.slice(0, 12)}…${result.resultSha256.slice(-10)}`,
  );
  addDefinitionRow(
    summary,
    "Arrotondamento",
    `grezzo € ${formatDecimalLimited(result.reference.calculation.compensationRaw, 6)} → ${euro(result.reference.calculation.compensationRounded)}`,
  );

  trace.replaceChildren(...result.reference.calculation.bands.map((band) => {
    const row = document.createElement("tr");
    const values = [
      { display: String(band.bandIndex + 1) },
      { display: euro(band.valueV) },
      { display: formatDecimal(band.qPrinted) },
      { display: formatDecimal(band.qApplied) },
      { display: formatDecimalLimited(band.parameterP, 8), raw: band.parameterP },
      { display: euro(band.compensationRounded) },
    ];
    values.forEach((value) => {
      const cell = document.createElement("td");
      cell.textContent = value.display;
      if (value.raw !== undefined) cell.title = value.raw;
      row.append(cell);
    });
    return row;
  }));

  warnings.replaceChildren(...result.warnings.map((warning) => {
    const item = document.createElement("li");
    item.textContent = warning;
    return item;
  }));
  warningBox.hidden = result.warnings.length === 0;
  source.textContent = `${result.source.publication} · ${result.source.locator} · SHA-256 ${result.source.sha256.slice(0, 12)}…${result.source.sha256.slice(-10)}`;
  output.hidden = false;
}

export function mountDm2016Lab(
  root: HTMLElement,
  options: Dm2016LabControllerOptions = {},
): Dm2016LabController | null {
  const catalog = getDm2016LabCatalog();
  root.innerHTML = `
    <section class="dm2016-lab wrap" aria-labelledby="dm2016-lab-title">
      <div class="dm2016-lab__heading">
        <div>
          <span class="eyebrow eyebrow--light">2 · Calcola il compenso parametrico</span>
          <h2 id="dm2016-lab-title">Inserisci i dati essenziali.</h2>
          <p>Scegli opera, prestazione e importo. Il sistema determina G e Q; formule, fonti e verifiche restano disponibili nei dettagli.</p>
        </div>
        <div class="dm2016-lab__lock"><span aria-hidden="true">✓</span><p><strong>Calcolo parametrico verificato</strong>Fonti ufficiali, esempi istituzionali, regressioni automatiche e 50 scenari di confronto. Non sostituisce la verifica dell’incarico e non è una certificazione legale automatica.</p></div>
      </div>
      <div class="dm2016-lab__grid">
        <form class="dm2016-lab__form" data-dm2016-form novalidate>
          <ol class="dm2016-quick-steps" aria-label="Tre dati necessari per il calcolo"><li><span>1</span>Opera</li><li><span>2</span>Prestazione</li><li><span>3</span>Importo</li></ol>
          <label>Categoria dell’opera<select data-dm2016-category></select></label>
          <label class="dm2016-field--wide">Destinazione funzionale<select data-dm2016-destination></select></label>
          <details class="dm2016-field-help"><summary>Dettagli della destinazione selezionata</summary><p data-dm2016-destination-detail></p></details>
          <label class="dm2016-field--wide">Prestazione<select data-dm2016-service></select></label>
          <details class="dm2016-field-help"><summary>Fase e verifica della prestazione</summary><p data-dm2016-service-detail></p></details>
          <label>Valore dell’opera (V)<span class="dm2016-money-input"><span>€</span><input data-dm2016-value inputmode="decimal" autocomplete="off" value="100000" placeholder="100000" required /></span></label>
          <label data-dm2016-inhabitants-field hidden>Popolazione interessata<span class="dm2016-money-input dm2016-money-input--unit"><input data-dm2016-inhabitants inputmode="numeric" autocomplete="off" value="30000" placeholder="30000" /><span>abitanti</span></span><small>È il selettore delle fasce; V resta il valore economico complessivo indicato dalla norma.</small></label>
          <label data-dm2016-planning-level-field hidden>Livello della pianificazione<select data-dm2016-planning-level><option value="general">Piano generale</option><option value="executive">Piano esecutivo o attuativo</option></select><small>Serve a determinare quale base economica può essere usata come valore V.</small></label>
          <label data-dm2016-planning-basis-field hidden>Come è stato determinato il valore V<select data-dm2016-planning-basis></select><small data-dm2016-planning-basis-detail></small></label>
          <label data-dm2016-multiplier-field hidden><span data-dm2016-multiplier-label>Numero di addetti</span><input data-dm2016-multiplier type="number" inputmode="numeric" autocomplete="off" min="1" step="1" value="1" /><small data-dm2016-multiplier-detail>La prestazione è prevista per ogni addetto.</small></label>
          <fieldset class="dm2016-lab__procedural" data-dm2016-procedural hidden>
            <legend>Condizioni procedurali della prestazione</legend>
            <p data-dm2016-procedural-detail>Le risposte determinano se la prestazione è applicabile.</p>
            <label data-dm2016-procurement-field hidden>Modello di affidamento<select data-dm2016-procurement><option value="">Seleziona</option><option value="non_integrated">Procedura non integrata</option><option value="integrated">Appalto integrato</option></select></label>
            <label data-dm2016-tender-base-field hidden>Il progetto è posto a base di gara?<select data-dm2016-tender-base><option value="">Seleziona</option><option value="yes">Sì</option><option value="no">No</option></select></label>
            <label data-dm2016-concession-field hidden>È una concessione di lavori pubblici?<select data-dm2016-concession><option value="">Seleziona</option><option value="yes">Sì</option><option value="no">No</option></select></label>
          </fieldset>
          <details class="dm2016-lab__advanced"><summary>Metodo di calcolo e lettura normativa</summary><label>Modalità di lettura<select data-dm2016-mode><option value="printed_source_strict">Default normativo · testo stampato</option><option value="professionearchitetto_v0_74">Compatibilità CNI / ProfessioneArchitetto v0.74</option></select><small data-dm2016-mode-detail>Applica la lettura letterale dei coefficienti e delle condizioni stampate nella fonte ufficiale. Per QcI.09 usa 0,012; non risulta una rettifica normativa formale.</small></label></details>
          <button type="submit">Calcola il compenso</button>
          <p class="dm2016-lab__error" data-dm2016-error role="alert" hidden></p>
        </form>
        <section class="dm2016-lab__result" data-dm2016-output aria-live="polite" hidden>
          <span>Compenso parametrico della prestazione</span>
          <strong data-dm2016-amount></strong>
          <details class="dm2016-result-details">
            <summary>Vedi dati, metodo e impronta del calcolo</summary>
            <dl data-dm2016-summary></dl>
          </details>
          <div class="dm2016-lab__warning" data-dm2016-warning-box hidden><strong>Punto da non ignorare</strong><ul data-dm2016-warnings></ul></div>
        </section>
      </div>
      <details class="dm2016-trace" data-dm2016-output hidden>
        <summary><span>Vedi il calcolo fascia per fascia</span><strong>V × G × Q × P</strong></summary>
        <div class="dm2016-trace__scroll"><table><thead><tr><th>Fascia</th><th>V applicato</th><th>Q stampato</th><th>Q applicato</th><th>P</th><th>Compenso</th></tr></thead><tbody data-dm2016-trace></tbody></table></div>
        <p data-dm2016-source></p>
      </details>
    </section>`;

  const form = root.querySelector<HTMLFormElement>("[data-dm2016-form]");
  const category = root.querySelector<HTMLSelectElement>("[data-dm2016-category]");
  const destination = root.querySelector<HTMLSelectElement>("[data-dm2016-destination]");
  const destinationDetail = root.querySelector<HTMLElement>("[data-dm2016-destination-detail]");
  const service = root.querySelector<HTMLSelectElement>("[data-dm2016-service]");
  const serviceDetail = root.querySelector<HTMLElement>("[data-dm2016-service-detail]");
  const value = root.querySelector<HTMLInputElement>("[data-dm2016-value]");
  const inhabitantsField = root.querySelector<HTMLElement>("[data-dm2016-inhabitants-field]");
  const inhabitants = root.querySelector<HTMLInputElement>("[data-dm2016-inhabitants]");
  const planningLevelField = root.querySelector<HTMLElement>("[data-dm2016-planning-level-field]");
  const planningLevel = root.querySelector<HTMLSelectElement>("[data-dm2016-planning-level]");
  const planningBasisField = root.querySelector<HTMLElement>("[data-dm2016-planning-basis-field]");
  const planningBasis = root.querySelector<HTMLSelectElement>("[data-dm2016-planning-basis]");
  const planningBasisDetail = root.querySelector<HTMLElement>("[data-dm2016-planning-basis-detail]");
  const multiplierField = root.querySelector<HTMLElement>("[data-dm2016-multiplier-field]");
  const multiplierLabel = root.querySelector<HTMLElement>("[data-dm2016-multiplier-label]");
  const multiplierDetail = root.querySelector<HTMLElement>("[data-dm2016-multiplier-detail]");
  const multiplier = root.querySelector<HTMLInputElement>("[data-dm2016-multiplier]");
  const procedural = root.querySelector<HTMLFieldSetElement>("[data-dm2016-procedural]");
  const proceduralDetail = root.querySelector<HTMLElement>("[data-dm2016-procedural-detail]");
  const procurementField = root.querySelector<HTMLElement>("[data-dm2016-procurement-field]");
  const procurement = root.querySelector<HTMLSelectElement>("[data-dm2016-procurement]");
  const tenderBaseField = root.querySelector<HTMLElement>("[data-dm2016-tender-base-field]");
  const tenderBase = root.querySelector<HTMLSelectElement>("[data-dm2016-tender-base]");
  const concessionField = root.querySelector<HTMLElement>("[data-dm2016-concession-field]");
  const concession = root.querySelector<HTMLSelectElement>("[data-dm2016-concession]");
  const mode = root.querySelector<HTMLSelectElement>("[data-dm2016-mode]");
  const modeDetail = root.querySelector<HTMLElement>("[data-dm2016-mode-detail]");
  const error = root.querySelector<HTMLElement>("[data-dm2016-error]");
  if (
    form === null || category === null || destination === null
    || destinationDetail === null || service === null || serviceDetail === null
    || value === null || inhabitantsField === null || inhabitants === null
    || planningLevelField === null || planningLevel === null
    || planningBasisField === null || planningBasis === null
    || planningBasisDetail === null
    || multiplierField === null || multiplierLabel === null || multiplierDetail === null
    || multiplier === null || procedural === null || proceduralDetail === null
    || procurementField === null || procurement === null
    || tenderBaseField === null || tenderBase === null
    || concessionField === null || concession === null || mode === null
    || modeDetail === null
    || error === null
  ) return null;

  category.replaceChildren(option("", "Seleziona..."), ...catalog.categories.map((item) => option(
    item.code,
    `${item.label} · ${item.code}`,
  )));

  const updatePlanningBasis = (): void => {
    const previous = planningBasis.value;
    const choices = planningLevel.value === "general"
      ? [{
        value: "territorial_gdp",
        label: "Prodotto interno lordo del territorio interessato",
      }]
      : [
        {
          value: "existing_and_design_volumes",
          label: "Volume della situazione esistente e di progetto",
        },
        {
          value: "farm_gross_saleable_production",
          label: "Produzione lorda vendibile fondiaria",
        },
      ];
    planningBasis.replaceChildren(...choices.map((item) => option(item.value, item.label)));
    if (choices.some((item) => item.value === previous)) planningBasis.value = previous;
    planningBasisDetail.textContent = planningLevel.value === "general"
      ? "Per i piani generali la nota 2 collega V al PIL del territorio interessato."
      : "Per i piani esecutivi la nota 2 ammette volumi esistenti e di progetto oppure produzione lorda vendibile fondiaria.";
  };
  updatePlanningBasis();

  const updateServiceDetails = (
    services = getDm2016LabServices(destination.value),
  ): void => {
    const selected = services.find((item) => item.code === service.value);
    const planningService = selected?.planningContextRequired === true;
    inhabitantsField.hidden = selected?.selectorKind !== "population";
    inhabitants.required = selected?.selectorKind === "population";
    planningLevelField.hidden = !planningService;
    planningBasisField.hidden = !planningService;
    planningLevel.required = planningService;
    planningBasis.required = planningService;
    if (selected?.planningLevelRequirement !== null && selected?.planningLevelRequirement !== undefined) {
      planningLevel.value = selected.planningLevelRequirement;
    }
    planningLevel.disabled = selected?.planningLevelRequirement !== null
      && selected?.planningLevelRequirement !== undefined;
    updatePlanningBasis();
    multiplierField.hidden = selected?.multiplierKind !== "staff_count";
    multiplier.required = selected?.multiplierKind === "staff_count";
    multiplierLabel.textContent = selected?.multiplierLabel ?? "Numero di addetti";
    multiplierDetail.textContent = selected === undefined
      ? "La prestazione è prevista per ogni addetto."
      : `${selected.code} è prevista per ogni addetto.`;
    const proceduralInputs = new Set(selected?.proceduralInputs ?? []);
    procedural.hidden = proceduralInputs.size === 0;
    procurementField.hidden = !proceduralInputs.has("procurement_model");
    tenderBaseField.hidden = !proceduralInputs.has("tender_base_project");
    concessionField.hidden = !proceduralInputs.has("public_works_concession");
    procurement.required = !procurementField.hidden;
    tenderBase.required = !tenderBaseField.hidden;
    concession.required = !concessionField.hidden;
    proceduralDetail.textContent = selected === undefined
      ? "Le risposte determinano se la prestazione è applicabile."
      : `Verifica di applicabilità prevista dalla ${selected.footnoteRefs.length === 1 ? "nota" : "note"} ${selected.footnoteRefs.join(", ")}. Nessuna condizione viene presunta.`;
    serviceDetail.textContent = selected === undefined
      ? "Nessuna prestazione del catalogo attivo è applicabile a questa destinazione."
      : selected.comparisonCaseCount > 0
        ? `${selected.phaseLabel} · ${selected.comparisonCaseCount} ${selected.comparisonCaseCount === 1 ? "scenario pubblico confrontato" : "scenari pubblici confrontati"}`
        : `${selected.phaseLabel} · coefficiente Q dalla Tavola Z-2 ufficiale`;
    const qci09 = selected?.code === "QcI.09";
    modeDetail.textContent = qci09
      ? mode.value === "printed_source_strict"
        ? "Applica 0,012 sull’eccedenza di € 500.000 per Edilizia e Strutture, come stampato nel D.M. 17/06/2016. Il CNI lo considera un refuso e indica 0,12; non risulta una rettifica normativa formale."
        : "Modalità alternativa di compatibilità: applica 0,12 sull’eccedenza di € 500.000 per Edilizia e Strutture. La Circolare CNI n. 893/2022 segnala 0,012 come refuso; non risulta una rettifica normativa formale."
      : mode.value === "printed_source_strict"
        ? "Applica la lettura letterale dei coefficienti e delle condizioni stampate nella fonte ufficiale."
        : "Applica il metodo operativo verificato sulle fonti, sugli esempi istituzionali e sui 50 scenari pubblici di confronto.";
  };

  const updateServices = (): void => {
    const services = getDm2016LabServices(destination.value);
    service.replaceChildren(option("", "Seleziona..."), ...services.map((item) => option(
      item.code,
      `${item.code} · ${item.label}`,
    )));
    service.disabled = services.length === 0 || destination.value === "";
    updateServiceDetails(services);
  };

  const updateDestinationDetail = (): void => {
    const selected = catalog.categories.flatMap((item) => item.destinations)
      .find((item) => item.code === destination.value);
    destinationDetail.textContent = selected === undefined
      ? ""
      : `${selected.description} · coefficiente G ${formatDecimal(selected.complexityG)}`;
    updateServices();
  };

  const updateDestinations = (): void => {
    const selectedCategory = catalog.categories.find((item) => item.code === category.value);
    destination.replaceChildren(option("", "Seleziona..."), ...(selectedCategory?.destinations ?? []).map((item) => option(
      item.code,
      `${item.code} · G ${formatDecimal(item.complexityG)}`,
    )));
    destination.disabled = selectedCategory === undefined;
    updateDestinationDetail();
  };

  const invalidate = (): void => {
    root.querySelectorAll<HTMLElement>("[data-dm2016-output]").forEach((item) => {
      item.hidden = true;
    });
    options.onInvalid?.();
  };
  category.addEventListener("change", () => {
    updateDestinations();
    invalidate();
  });
  destination.addEventListener("change", () => {
    updateDestinationDetail();
    invalidate();
  });
  service.addEventListener("change", () => {
    updateServiceDetails();
    invalidate();
  });
  value.addEventListener("input", invalidate);
  inhabitants.addEventListener("input", invalidate);
  planningLevel.addEventListener("change", () => {
    updatePlanningBasis();
    invalidate();
  });
  planningBasis.addEventListener("change", invalidate);
  multiplier.addEventListener("input", invalidate);
  procurement.addEventListener("change", invalidate);
  tenderBase.addEventListener("change", invalidate);
  concession.addEventListener("change", invalidate);
  mode.addEventListener("change", () => {
    updateServiceDetails();
    invalidate();
  });
  updateDestinations();

  const hasOption = (element: HTMLSelectElement, optionValue: string): boolean =>
    Array.from(element.options).some((item) => item.value === optionValue);

  const controller: Dm2016LabController = {
    captureDraft: () => ({
      categoryCode: category.value,
      destinationCode: destination.value,
      serviceCode: service.value,
      valueV: value.value,
      inhabitants: inhabitants.value,
      multiplier: multiplier.value,
      planningLevel: planningLevel.value as NonNullable<Dm2016LabDraft["planningLevel"]>,
      planningValueBasis: planningBasis.value as NonNullable<Dm2016LabDraft["planningValueBasis"]>,
      ...(procurement.value === ""
        ? {}
        : { procurementModel: procurement.value as NonNullable<Dm2016LabDraft["procurementModel"]> }),
      ...(tenderBase.value === ""
        ? {}
        : { tenderBaseProject: tenderBase.value === "yes" }),
      ...(concession.value === ""
        ? {}
        : { publicWorksConcession: concession.value === "yes" }),
      interpretationMode: mode.value as Dm2016LabDraft["interpretationMode"],
    }),
    restoreDraft: (draft) => {
      if (!hasOption(category, draft.categoryCode)) return false;
      category.value = draft.categoryCode;
      updateDestinations();
      if (!hasOption(destination, draft.destinationCode)) return false;
      destination.value = draft.destinationCode;
      updateDestinationDetail();
      if (!hasOption(service, draft.serviceCode)) return false;
      service.value = draft.serviceCode;
      updateServiceDetails();
      if (!hasOption(mode, draft.interpretationMode)) return false;
      value.value = draft.valueV;
      inhabitants.value = draft.inhabitants;
      multiplier.value = draft.multiplier;
      procurement.value = draft.procurementModel ?? "";
      tenderBase.value = draft.tenderBaseProject === undefined
        ? ""
        : draft.tenderBaseProject ? "yes" : "no";
      concession.value = draft.publicWorksConcession === undefined
        ? ""
        : draft.publicWorksConcession ? "yes" : "no";
      const restoredService = getDm2016LabServices(destination.value)
        .find((item) => item.code === service.value);
      if (
        restoredService?.planningLevelRequirement === null
        && draft.planningLevel !== undefined
        && hasOption(planningLevel, draft.planningLevel)
      ) {
        planningLevel.value = draft.planningLevel;
        updatePlanningBasis();
      }
      if (
        draft.planningValueBasis !== undefined
        && hasOption(planningBasis, draft.planningValueBasis)
      ) planningBasis.value = draft.planningValueBasis;
      mode.value = draft.interpretationMode;
      invalidate();
      return true;
    },
    setEnabled: (enabled, reason) => {
      form.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLButtonElement>("input, select, button")
        .forEach((control) => { control.disabled = !enabled; });
      form.setAttribute("aria-disabled", enabled ? "false" : "true");
      if (!enabled) {
        invalidate();
        error.textContent = reason ?? "Completa lo screening normativo per abilitare il percorso D.M. 2016.";
        error.hidden = false;
      } else {
        error.hidden = true;
        destination.disabled = category.value === "";
        service.disabled = destination.value === "";
      }
    },
  };

  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    error.hidden = true;
    try {
      if (!procurementField.hidden && procurement.value === "") {
        throw new Error("Indica il modello di affidamento per verificare la prestazione");
      }
      if (!tenderBaseField.hidden && tenderBase.value === "") {
        throw new Error("Indica se il progetto è posto a base di gara");
      }
      if (!concessionField.hidden && concession.value === "") {
        throw new Error("Indica se si tratta di una concessione di lavori pubblici");
      }
      const result = await calculateDm2016LabWithHashes({
        destinationCode: destination.value,
        serviceCode: service.value as Dm2016LabService["code"],
        valueV: value.value.trim().replace(",", "."),
        ...(inhabitantsField.hidden
          ? {}
          : { inhabitants: inhabitants.value.trim().replace(",", ".") }),
        ...(multiplierField.hidden
          ? {}
          : { multiplier: multiplier.value.trim().replace(",", ".") }),
        ...(planningLevelField.hidden
          ? {}
          : {
            planning: {
              level: planningLevel.value as "general" | "executive",
              valueBasis: planningBasis.value as NonNullable<Dm2016LabDraft["planningValueBasis"]>,
            },
          }),
        ...(procurementField.hidden
          ? {}
          : {
            procurementModel: procurement.value as "non_integrated" | "integrated",
          }),
        ...(tenderBaseField.hidden
          ? {}
          : { tenderBaseProject: tenderBase.value === "yes" }),
        ...(concessionField.hidden
          ? {}
          : { publicWorksConcession: concession.value === "yes" }),
        interpretationMode: mode.value as Dm2016ReferenceInterpretationMode,
      });
      root.querySelectorAll<HTMLElement>("[data-dm2016-output]").forEach((item) => {
        item.hidden = false;
      });
      renderResult(root, result);
      options.onResult?.(result);
      const primaryOutput = root.querySelector<HTMLElement>(".dm2016-lab__result");
      if (
        primaryOutput !== null
        && window.matchMedia?.("(max-width: 820px)").matches === true
        && typeof primaryOutput.scrollIntoView === "function"
      ) {
        const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
        primaryOutput.scrollIntoView({
          behavior: reducedMotion ? "auto" : "smooth",
          block: "start",
        });
      }
    } catch (cause) {
      options.onInvalid?.();
      root.querySelectorAll<HTMLElement>("[data-dm2016-output]").forEach((item) => {
        item.hidden = true;
      });
      error.textContent = cause instanceof Error
        ? cause.message
        : "Il calcolo è stato bloccato perché i dati non sono validi.";
      error.hidden = false;
    }
  });
  return controller;
}
